import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Award } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const PALETTE = ["#f7c935", "#3b82f6", "#10b981", "#a855f7", "#f97316", "#ec4899"];

export default function CertifStatsTab({ api }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/certif/stats").then(r => setStats(r.data)).catch(() => setStats({ certifications: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stats) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  const withSessions = (stats.certifications || []).filter(c => (c.sessions || []).length > 0);
  if (withSessions.length === 0) return (
    <div className="text-center py-16 bg-[#14101c] border border-dashed border-white/10 rounded-xl" data-testid="stats-empty">
      <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-600" />
      <p className="text-sm text-slate-400 font-medium">Aucune session clôturée pour le moment.</p>
      <p className="text-[11px] text-slate-500 mt-1">Les statistiques de réussite apparaîtront après la clôture de vos premières sessions d'examen.</p>
    </div>
  );

  const dates = [...new Set(withSessions.flatMap(c => c.sessions.map(s => s.date_examen)))].sort();
  const chart = dates.map(d => {
    const row = { date: d };
    withSessions.forEach(c => {
      const s = c.sessions.find(x => x.date_examen === d);
      if (s && s.taux !== null) row[c.label] = s.taux;
    });
    return row;
  });

  return (
    <div className="space-y-4" data-testid="certif-stats-tab">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {withSessions.map((c, i) => (
          <div key={c.certification_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`stats-cert-card-${c.certification_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4" style={{ color: PALETTE[i % PALETTE.length] }} />
              <p className="text-sm font-bold text-white truncate flex-1">{c.label}</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black tabular-nums" style={{ color: PALETTE[i % PALETTE.length] }}>
                {c.taux_global === null ? "—" : `${c.taux_global}%`}
              </span>
              <span className="text-[10px] uppercase text-slate-500 font-bold">de réussite</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {c.sessions.length} session(s) clôturée(s) · {c.certifies}/{c.evalues} candidat(s) certifié(s)
            </p>
          </div>
        ))}
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <p className="text-[10px] uppercase text-slate-500 font-bold mb-3">Évolution du taux de réussite par session</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip formatter={v => `${v}%`} contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {withSessions.map((c, i) => (
                <Line key={c.certification_id} type="monotone" dataKey={c.label} stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2} connectNulls dot={{ r: 4 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 overflow-x-auto">
        <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">Détail des sessions clôturées</p>
        <table className="w-full text-xs" data-testid="stats-sessions-table">
          <thead>
            <tr className="border-b border-white/10 text-slate-500 uppercase text-[10px]">
              <th className="text-left py-1.5 px-2">Date</th>
              <th className="text-left py-1.5 px-2">Certification</th>
              <th className="text-right py-1.5 px-2">Évalués</th>
              <th className="text-right py-1.5 px-2">Certifiés</th>
              <th className="text-right py-1.5 px-2">Ajournés</th>
              <th className="text-right py-1.5 px-2">Absents</th>
              <th className="text-right py-1.5 px-2">Taux</th>
            </tr>
          </thead>
          <tbody>
            {withSessions.flatMap(c => c.sessions.map(s => ({ ...s, label: c.label })))
              .sort((a, b) => (b.date_examen || "").localeCompare(a.date_examen || ""))
              .map(s => (
                <tr key={s.session_id} className="border-b border-white/5">
                  <td className="py-1.5 px-2 text-slate-300">{s.date_examen}</td>
                  <td className="py-1.5 px-2 text-white">{s.label}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-300">{s.evalues}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-emerald-400">{s.certifies}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-amber-400">{s.ajournes}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">{s.absents}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-bold text-white">{s.taux === null ? "—" : `${s.taux}%`}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
